import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, Lock, Loader2 } from "lucide-react";
import api, { fmtErr, setToken } from "@/lib/api";
import { useFetch, PageHeader, Loading, ErrorState, SectionCard } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw !== cf) { toast.error("New password and confirmation do not match"); return; }
    if (nw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw, confirm_password: cf });
      toast.success("Password changed. Please sign in again.");
      setToken(null);
      setTimeout(() => window.location.assign("/login"), 1200);
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  return (
    <SectionCard className="p-5" data-testid="settings-change-password">
      <div className="flex items-center gap-2 mb-3"><Lock className="h-4 w-4 text-primary" /><h3 className="font-semibold font-outfit">Change Password</h3></div>
      <p className="text-xs text-muted-foreground mb-4">For security, changing your password signs you out of all sessions.</p>
      <form onSubmit={submit} className="space-y-3 max-w-sm" data-testid="change-password-form">
        <div><Label className="text-xs">Current password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required autoComplete="current-password" className="mt-1" data-testid="cp-current-input" /></div>
        <div><Label className="text-xs">New password</Label><Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} required autoComplete="new-password" className="mt-1" data-testid="cp-new-input" /></div>
        <div><Label className="text-xs">Confirm new password</Label><Input type="password" value={cf} onChange={(e) => setCf(e.target.value)} required autoComplete="new-password" className="mt-1" data-testid="cp-confirm-input" /></div>
        <Button type="submit" size="sm" disabled={busy} data-testid="cp-submit-btn">{busy ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Updating…</> : "Update password"}</Button>
      </form>
    </SectionCard>
  );
}

function Row({ label, children, hint }) {
  return (
    <div className="grid sm:grid-cols-2 gap-2 items-center py-2">
      <div><div className="text-sm font-medium">{label}</div>{hint && <div className="text-xs text-muted-foreground">{hint}</div>}</div>
      <div>{children}</div>
    </div>
  );
}

export default function Settings() {
  const { data, loading, error, refetch } = useFetch("/admin/settings", []);
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setS(data); }, [data]);

  if (loading || !s) return <div><PageHeader title="Marketplace Settings" /><Loading /></div>;
  if (error) return <div><PageHeader title="Marketplace Settings" /><ErrorState error={error} onRetry={refetch} /></div>;

  const num = (v) => (v === "" ? 0 : Number(v));
  const upd = (section, patch) => setS((p) => ({ ...p, [section]: { ...p[section], ...patch } }));

  const save = async (section) => {
    setBusy(true);
    try { await api.patch("/admin/settings", { patch: { [section]: s[section] } }); toast.success("Settings saved"); refetch(); }
    catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const savePlatformFee = async () => {
    const p = s.platform_fee || {};
    if (Number(p.max) < Number(p.min)) { toast.error("Maximum must be ≥ Minimum"); return; }
    if (Number(p.max) > 9) { toast.error("Maximum cannot exceed ₹9"); return; }
    if (Number(p.min) < 0 || Number(p.threshold) < 0) { toast.error("Values cannot be negative"); return; }
    setBusy(true);
    try {
      await api.put("/admin/settings", {
        platform_fee_enabled: !!p.enabled, platform_fee_min: Number(p.min),
        platform_fee_max: Number(p.max), platform_fee_threshold: Number(p.threshold),
      });
      toast.success("Platform fee saved"); refetch();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const rules = s.free_delivery?.rules || [];
  const setRule = (i, key, val) => {
    const next = rules.map((r, idx) => (idx === i ? { ...r, [key]: val === "" ? null : Number(val) } : r));
    upd("free_delivery", { rules: next });
  };

  return (
    <div>
      <PageHeader title="Marketplace Settings" subtitle="Configure delivery, charges, tax, referral and loyalty rules" />
      <div className="grid gap-6 lg:grid-cols-2">
        <SectionCard className="p-5" data-testid="settings-platform-fee">
          <div className="flex items-center justify-between mb-3"><h3 className="font-semibold font-outfit">Fees &amp; Charges (Platform Fee)</h3><Button size="sm" onClick={savePlatformFee} disabled={busy} data-testid="save-platform-fee-btn"><Save className="h-3.5 w-3.5 mr-1.5" /> Save</Button></div>
          <Row label="Enabled" hint="Turn the platform fee on or off"><input type="checkbox" checked={!!s.platform_fee?.enabled} onChange={(e) => upd("platform_fee", { enabled: e.target.checked })} className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid="pf-enabled" /></Row>
          <Row label="Minimum ₹" hint="Applied below threshold"><Input type="number" value={s.platform_fee?.min ?? 3} onChange={(e) => upd("platform_fee", { min: num(e.target.value) })} data-testid="pf-min" /></Row>
          <Row label="Maximum ₹" hint="Applied at/above threshold (max ₹9)"><Input type="number" value={s.platform_fee?.max ?? 9} onChange={(e) => upd("platform_fee", { max: num(e.target.value) })} data-testid="pf-max" /></Row>
          <Row label="Threshold ₹" hint="Subtotal cutoff"><Input type="number" value={s.platform_fee?.threshold ?? 300} onChange={(e) => upd("platform_fee", { threshold: num(e.target.value) })} data-testid="pf-threshold" /></Row>
        </SectionCard>

        <SectionCard className="p-5" data-testid="settings-free-delivery">
          <div className="flex items-center justify-between mb-3"><h3 className="font-semibold font-outfit">Free Delivery Rules</h3><Button size="sm" onClick={() => save("free_delivery")} disabled={busy} data-testid="save-free-delivery-btn"><Save className="h-3.5 w-3.5 mr-1.5" /> Save</Button></div>
          {rules.map((r, i) => (
            <div key={i} className="grid grid-cols-3 gap-2 mb-2 items-end">
              <div><Label className="text-xs">From order #</Label><Input type="number" value={r.from_index ?? ""} onChange={(e) => setRule(i, "from_index", e.target.value)} className="mt-1" data-testid={`fd-from-${i}`} /></div>
              <div><Label className="text-xs">To order #</Label><Input type="number" value={r.to_index ?? ""} onChange={(e) => setRule(i, "to_index", e.target.value)} placeholder="∞" className="mt-1" data-testid={`fd-to-${i}`} /></div>
              <div><Label className="text-xs">Min ₹</Label><Input type="number" value={r.min_value ?? 0} onChange={(e) => setRule(i, "min_value", e.target.value)} className="mt-1" data-testid={`fd-min-${i}`} /></div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground mt-2">Leave “To order #” empty for 4th+ orders. Eligibility uses completed order history.</p>
        </SectionCard>

        <SectionCard className="p-5" data-testid="settings-delivery-charge">
          <div className="flex items-center justify-between mb-3"><h3 className="font-semibold font-outfit">Delivery Charge</h3><Button size="sm" onClick={() => save("delivery_charge")} disabled={busy} data-testid="save-delivery-charge-btn"><Save className="h-3.5 w-3.5 mr-1.5" /> Save</Button></div>
          <Row label="Base ₹"><Input type="number" value={s.delivery_charge?.base ?? 0} onChange={(e) => upd("delivery_charge", { base: num(e.target.value) })} data-testid="dc-base" /></Row>
          <Row label="Per km ₹"><Input type="number" value={s.delivery_charge?.per_km ?? 0} onChange={(e) => upd("delivery_charge", { per_km: num(e.target.value) })} data-testid="dc-perkm" /></Row>
          <Row label="Free under km"><Input type="number" value={s.delivery_charge?.min_km_free ?? 0} onChange={(e) => upd("delivery_charge", { min_km_free: num(e.target.value) })} data-testid="dc-freekm" /></Row>
          <Row label="Max charge ₹"><Input type="number" value={s.delivery_charge?.max_charge ?? 0} onChange={(e) => upd("delivery_charge", { max_charge: num(e.target.value) })} data-testid="dc-max" /></Row>
        </SectionCard>

        <SectionCard className="p-5" data-testid="settings-charges">
          <div className="flex items-center justify-between mb-3"><h3 className="font-semibold font-outfit">Platform Charge</h3><Button size="sm" onClick={() => save("platform_charge")} disabled={busy} data-testid="save-charges-btn"><Save className="h-3.5 w-3.5 mr-1.5" /> Save</Button></div>
          <Row label="Platform charge ₹" hint="Fixed fee per order"><Input type="number" value={s.platform_charge?.value ?? 0} onChange={(e) => upd("platform_charge", { value: num(e.target.value) })} data-testid="pc-value" /></Row>
          <p className="text-xs text-muted-foreground mt-2">Commission rates are managed on the <a href="/commission" className="text-primary underline" data-testid="settings-commission-link">Commission</a> page.</p>
        </SectionCard>

        <SectionCard className="p-5" data-testid="settings-tax">
          <div className="flex items-center justify-between mb-3"><h3 className="font-semibold font-outfit">Tax (GST)</h3><Button size="sm" onClick={() => save("tax")} disabled={busy} data-testid="save-tax-btn"><Save className="h-3.5 w-3.5 mr-1.5" /> Save</Button></div>
          <Row label="Default GST %" hint="Used when product has none"><Input type="number" value={s.tax?.default_gst_rate ?? 0} onChange={(e) => upd("tax", { default_gst_rate: num(e.target.value) })} data-testid="tax-rate" /></Row>
          <Row label="Tax inclusive"><input type="checkbox" checked={!!s.tax?.inclusive} onChange={(e) => upd("tax", { inclusive: e.target.checked })} className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid="tax-inclusive" /></Row>
        </SectionCard>

        <SectionCard className="p-5" data-testid="settings-referral">
          <div className="flex items-center justify-between mb-3"><h3 className="font-semibold font-outfit">Referral</h3><Button size="sm" onClick={() => save("referral")} disabled={busy} data-testid="save-referral-btn"><Save className="h-3.5 w-3.5 mr-1.5" /> Save</Button></div>
          <Row label="Milestone count"><Input type="number" value={s.referral?.milestone_count ?? 0} onChange={(e) => upd("referral", { milestone_count: num(e.target.value) })} data-testid="ref-milestone" /></Row>
          <Row label="Reward points"><Input type="number" value={s.referral?.reward_points ?? 0} onChange={(e) => upd("referral", { reward_points: num(e.target.value) })} data-testid="ref-points" /></Row>
          <Row label="Points per ₹"><Input type="number" value={s.referral?.points_per_rupee ?? 0} onChange={(e) => upd("referral", { points_per_rupee: num(e.target.value) })} data-testid="ref-ppr" /></Row>
        </SectionCard>

        <SectionCard className="p-5" data-testid="settings-loyalty">
          <div className="flex items-center justify-between mb-3"><h3 className="font-semibold font-outfit">Loyalty</h3><Button size="sm" onClick={() => save("loyalty")} disabled={busy} data-testid="save-loyalty-btn"><Save className="h-3.5 w-3.5 mr-1.5" /> Save</Button></div>
          <Row label="Points per ₹" hint="100 = ₹1"><Input type="number" value={s.loyalty?.points_per_rupee ?? 0} onChange={(e) => upd("loyalty", { points_per_rupee: num(e.target.value) })} data-testid="loy-ppr" /></Row>
          <Row label="Min redeem points"><Input type="number" value={s.loyalty?.min_redeem_points ?? 0} onChange={(e) => upd("loyalty", { min_redeem_points: num(e.target.value) })} data-testid="loy-min" /></Row>
        </SectionCard>
      </div>

      <div className="mt-6">
        <ChangePasswordCard />
      </div>
    </div>
  );
}
